import dynamic from 'next/dynamic';
import { sanitizeHTML } from "@/lib/utils";

const AnimatedSection = dynamic(() => import('../../components/Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface WelcomeData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
}

export const fields = [
  "heading.text",
  "heading.title",
  "text.text",
] as const;

interface WelcomeProps {
  data?: WelcomeData;
  zIndex: number;
  thresholds: number[][];
  mobileThresholds: number[][];
  transitionTime?: number;
  helperRotation?: number;
}

export default function Welcome({ data, zIndex, thresholds, mobileThresholds, transitionTime, helperRotation }: WelcomeProps) {
  return (
    <ScreenAwareContent
	    supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32 lg:bg-primary top-0 left-0 text-base"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[thresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { y: 0, opacity: 1 },
              above: { y: "100vh", opacity: 1 },
              below: { y: "-100vh", opacity: 1 }
            },
            mobile: {
              visible: { y: 0, opacity: 1 },
              above: { y: "100vh", opacity: 1 },
              below: { y: 0, opacity: 0 }
            }
          }}
        >
          <div className="gl-desktop-scale w-full h-full">

            {/* Background - only visible on lg+ screens */}
            <div className="bg-primary lg:block inset-0 lg:bg-primary h-full w-full scale-100">
              <div className="gl-container relative h-full w-full">
                <div className="flex items-center h-full">
                  {/* Content container */}
                  <div className="flex flex-col gap-2.5 self-auto lg:m-auto text-center max-w-4xl relative">
                    {/* Heading */}
                    <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-light w-[100%] sm:w-[50%] lg:w-full pb-8 sm:pb-14">
                      {data?.heading?.title}
                    </h2>

                    {/* Description */}
                    <div 
                      className="text-[1rem] font-normal leading-normal sm:text-base lg:text-xl font-greycliff text-light lg:leading-loose tracking-tight w-[100%] sm:w-auto"
                      dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      }
      fallbackContent={
        <section className="overflow-hidden w-full xl:bg-primary text-base">
          <div className="w-full h-full">

            {/* Background - only visible on lg+ screens */}
            <div className="bg-primary xl:block inset-0 xl:bg-primary h-full w-full scale-100">
              <div className="gl-container relative h-full w-full">
                <div className="flex items-center h-full w-full">
                  {/* Content container */}
                  <div className="flex flex-col gap-2.5 xl:m-auto text-center relative h-full w-full">
                    {/* Heading */}
                    <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-light w-full pb-4 sm:pb-8">
                      {data?.heading?.title}
                    </h2>

                    {/* Description */}
                    <div 
                      className="text-[1rem] font-normal leading-normal sm:text-base xl:text-xl font-greycliff text-light xl:leading-loose tracking-tight w-[100%] sm:w-auto"
                      dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    />
  );
}
